import React, { useState, useRef, useCallback, useMemo } from 'react';
import { TextField, Paper, MenuList, MenuItem, ListItemIcon, ListItemText, Avatar, Popper, CircularProgress } from '@mui/material';
import { Groups as GroupsIcon, Diversity3 as ClubIcon, SmartToy as NereusIcon } from '@mui/icons-material';
import { TextFieldProps } from '@mui/material/TextField';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { groupService, DiveGroup } from '../services/groupService';
import { clubService, DiveClub } from '../services/clubService';

interface MentionOption {
  id: string;
  displayName: string;
  kind: 'user' | 'group' | 'club' | 'nereus';
  photoUrl?: string | null;
}

/**
 * Sentinel "user id" for tagging Nereus, matching NereusContextService.NereusUserId on
 * the backend. Anyone can tag him, buddy or not — whether he actually replies depends on
 * the tagger's own Nereus access, checked server-side.
 */
export const NEREUS_MENTION_ID = 'nereus';
const NEREUS_OPTION: MentionOption = { id: NEREUS_MENTION_ID, displayName: 'Nereus', kind: 'nereus' };

function initials(name: string) {
  return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/**
 * Finds the `@partial` mention token, if any, that the cursor is currently inside of.
 * The token must start at the beginning of the text or right after whitespace, so an
 * email address or an mid-word "@" never triggers the picker.
 */
function findActiveMentionToken(text: string, cursor: number): { start: number; query: string } | null {
  const uptoCursor = text.slice(0, cursor);
  const at = uptoCursor.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(uptoCursor[at - 1])) return null;

  const query = uptoCursor.slice(at + 1);
  if (/\s/.test(query)) return null; // the mention token ended at a space before the cursor

  return { start: at, query };
}

type MentionTextFieldProps = Omit<TextFieldProps, 'value' | 'onChange'> & {
  value: string;
  onChange: (text: string) => void;
  taggedUserIds: string[];
  onTaggedUserIdsChange: (ids: string[]) => void;
  taggedGroupIds: string[];
  onTaggedGroupIdsChange: (ids: string[]) => void;
  taggedClubIds: string[];
  onTaggedClubIdsChange: (ids: string[]) => void;
};

/**
 * A text field supporting @mention autocomplete for dive buddies, dive groups, and dive clubs.
 * Typing "@" (at the start of the text or after whitespace) opens a search dropdown;
 * picking an option inserts "@DisplayName " and records the id in the matching tagged-ids
 * list. Deleting a mention's text also drops it from the tagged-ids list, since the two
 * are reconciled against each other on every keystroke rather than tracked separately.
 */
const MentionTextField: React.FC<MentionTextFieldProps> = ({
  value, onChange, taggedUserIds, onTaggedUserIdsChange, taggedGroupIds, onTaggedGroupIdsChange,
  taggedClubIds, onTaggedClubIdsChange,
  ...textFieldProps
}) => {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  // Bumped on every keystroke/pick/close so a search that resolves after the dropdown
  // has already been dismissed can't reopen it with stale results.
  const searchRequestIdRef = useRef(0);
  const [mentionMap, setMentionMap] = useState<Record<string, MentionOption>>({});
  const [activeToken, setActiveToken] = useState<{ start: number; query: string } | null>(null);
  const [options, setOptions] = useState<MentionOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [myGroups, setMyGroups] = useState<DiveGroup[] | null>(null);
  const [myClubs, setMyClubs] = useState<DiveClub[] | null>(null);

  const ensureGroupsLoaded = useCallback(async () => {
    if (myGroups !== null) return myGroups;
    try {
      const groups = await groupService.getMyGroups();
      setMyGroups(groups);
      return groups;
    } catch {
      setMyGroups([]);
      return [];
    }
  }, [myGroups]);

  const ensureClubsLoaded = useCallback(async () => {
    if (myClubs !== null) return myClubs;
    try {
      const clubs = await clubService.getMyClubs();
      setMyClubs(clubs);
      return clubs;
    } catch {
      setMyClubs([]);
      return [];
    }
  }, [myClubs]);

  const runSearch = useCallback(async (query: string) => {
    const requestId = ++searchRequestIdRef.current;
    if (query.length === 0) {
      setOptions([]);
      return;
    }
    setSearching(true);
    try {
      const [users, groups, clubs] = await Promise.all([
        buddyService.searchUsers(query) as Promise<UserPublicProfile[]>,
        ensureGroupsLoaded(),
        ensureClubsLoaded(),
      ]);
      if (requestId !== searchRequestIdRef.current) return; // superseded by a later keystroke/pick

      const userOptions: MentionOption[] = users.map(u => ({
        id: u.userId, displayName: u.displayName, kind: 'user', photoUrl: u.profilePhotoUrl,
      }));
      const groupOptions: MentionOption[] = groups
        .filter(g => g.name.toLowerCase().includes(query.toLowerCase()))
        .map(g => ({ id: g.groupId, displayName: g.name, kind: 'group' }));
      const clubOptions: MentionOption[] = clubs
        .filter(c => c.name.toLowerCase().includes(query.toLowerCase()))
        .map(c => ({ id: c.clubId, displayName: c.name, kind: 'club' }));
      const nereusOptions: MentionOption[] = NEREUS_OPTION.displayName.toLowerCase().startsWith(query.toLowerCase())
        ? [NEREUS_OPTION]
        : [];
      setOptions([...nereusOptions, ...userOptions, ...groupOptions, ...clubOptions]);
      setHighlightIndex(0);
    } finally {
      if (requestId === searchRequestIdRef.current) setSearching(false);
    }
  }, [ensureGroupsLoaded, ensureClubsLoaded]);

  // Reconciles the tagged-id lists against which "@Name" tokens still literally
  // appear in the text, so deleting a mention's text un-tags it automatically.
  const reconcileTags = useCallback((nextText: string, map: Record<string, MentionOption>) => {
    const stillPresentIds = new Set(
      Object.values(map).filter(o => nextText.includes(`@${o.displayName}`)).map(o => o.id)
    );
    const nextUserIds = taggedUserIds.filter(id => stillPresentIds.has(id));
    const nextGroupIds = taggedGroupIds.filter(id => stillPresentIds.has(id));
    const nextClubIds = taggedClubIds.filter(id => stillPresentIds.has(id));
    if (nextUserIds.length !== taggedUserIds.length) onTaggedUserIdsChange(nextUserIds);
    if (nextGroupIds.length !== taggedGroupIds.length) onTaggedGroupIdsChange(nextGroupIds);
    if (nextClubIds.length !== taggedClubIds.length) onTaggedClubIdsChange(nextClubIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taggedUserIds, taggedGroupIds, taggedClubIds, onTaggedUserIdsChange, onTaggedGroupIdsChange, onTaggedClubIdsChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextText = e.target.value;
    const cursor = e.target.selectionStart ?? nextText.length;
    onChange(nextText);
    reconcileTags(nextText, mentionMap);

    const token = findActiveMentionToken(nextText, cursor);
    setActiveToken(token);
    if (token) runSearch(token.query);
    else setOptions([]);
  };

  const pickOption = (option: MentionOption) => {
    if (!activeToken) return;
    const before = value.slice(0, activeToken.start);
    const after = value.slice(activeToken.start + 1 + activeToken.query.length);
    const nextText = `${before}@${option.displayName} ${after}`;
    onChange(nextText);
    setMentionMap(prev => ({ ...prev, [option.id]: option }));

    if ((option.kind === 'user' || option.kind === 'nereus') && !taggedUserIds.includes(option.id)) {
      onTaggedUserIdsChange([...taggedUserIds, option.id]);
    }
    if (option.kind === 'group' && !taggedGroupIds.includes(option.id)) {
      onTaggedGroupIdsChange([...taggedGroupIds, option.id]);
    }
    if (option.kind === 'club' && !taggedClubIds.includes(option.id)) {
      onTaggedClubIdsChange([...taggedClubIds, option.id]);
    }

    searchRequestIdRef.current++; // invalidate any in-flight search so its result can't reopen the dropdown
    setActiveToken(null);
    setOptions([]);
    setSearching(false);

    // Restore focus and put the cursor right after the inserted mention.
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const caretPos = before.length + option.displayName.length + 2;
      el.setSelectionRange(caretPos, caretPos);
    });
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    const dropdownOpen = !!activeToken && options.length > 0;
    if (!dropdownOpen) {
      textFieldProps.onKeyDown?.(e);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(i => (i + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(i => (i - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pickOption(options[highlightIndex]);
    } else if (e.key === 'Escape') {
      searchRequestIdRef.current++;
      setActiveToken(null);
      setOptions([]);
    } else {
      textFieldProps.onKeyDown?.(e);
    }
  };

  const open = !!activeToken && (options.length > 0 || searching);

  const combinedInputRef = useMemo(() => (el: HTMLInputElement | HTMLTextAreaElement | null) => {
    inputRef.current = el;
  }, []);

  return (
    <>
      <TextField
        {...textFieldProps}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        inputRef={combinedInputRef}
      />
      <Popper open={open} anchorEl={inputRef.current} placement="bottom-start" style={{ zIndex: 1300, minWidth: 240 }}>
        <Paper elevation={4}>
          {searching && options.length === 0 ? (
            <MenuList dense>
              <MenuItem disabled>
                <CircularProgress size={16} sx={{ mr: 1 }} /> Searching…
              </MenuItem>
            </MenuList>
          ) : (
            <MenuList dense>
              {options.map((option, index) => (
                <MenuItem
                  key={`${option.kind}-${option.id}`}
                  selected={index === highlightIndex}
                  onMouseEnter={() => setHighlightIndex(index)}
                  onClick={() => pickOption(option)}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {option.kind === 'group' ? (
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'secondary.main' }}>
                        <GroupsIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                    ) : option.kind === 'club' ? (
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                        <ClubIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                    ) : option.kind === 'nereus' ? (
                      <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                        <NereusIcon sx={{ fontSize: 14 }} />
                      </Avatar>
                    ) : (
                      <Avatar src={option.photoUrl ?? undefined} sx={{ width: 24, height: 24, fontSize: 11 }}>
                        {initials(option.displayName) || '?'}
                      </Avatar>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.displayName}
                    secondary={option.kind === 'group' ? 'Group' : option.kind === 'club' ? 'Club' : option.kind === 'nereus' ? 'AI dive instructor — replies in the thread' : undefined}
                  />
                </MenuItem>
              ))}
            </MenuList>
          )}
        </Paper>
      </Popper>
    </>
  );
};

export default MentionTextField;
